import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, BookHeart, Camera, NotebookPen, Plus, ScanText, Settings2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { isMeal, MEAL_NAMES, MEALS, type Meal } from '../../shared/meals'
import { NewCookbook } from '@/components/CookbooksSection'
import { ConfirmButton } from '@/components/ConfirmButton'
import { FormError } from '@/components/FormError'
import { page } from '@/components/layout'
import { CreateAction } from '@/components/CreateAction'
import { ModernBook } from '@/components/recipes/ModernBook'
import { RecipeTile } from '@/components/RecipeTile'
import { Avatar } from '@/components/tree/Avatar'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'
import { useFamily } from '@/lib/families'
import { useItemHome, usePageFrame } from '@/lib/frame'
import { forgetVisit, rememberVisit } from '@/lib/recent'
import { useBook, type Book, type From } from '@/lib/recipes'
import { useMe } from '@/lib/useMe'
import { cn } from '@/lib/utils'
import { ShareWith } from '@/components/ShareWith'
import { audienceBody, audienceFrom, audienceSaid, type Audience } from '@/lib/spaces'
import { PageLoader } from '@/components/Loader'

export function BookPage() {
  const { id = '' } = useParams()
  const book = useBook(id)
  if (book.isPending) return <PageLoader />
  if (book.isError) return <FormError error={book.error} />
  return <BookView book={book.data} />
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

// A book laid out the way an album is: its name over its recipes, each one the photo of its card.
function BookView({ book }: { book: Book }) {
  const navigate = useNavigate()
  const [settings, setSettings] = useState(false)
  const [cookbook, setCookbook] = useState(false)
  const family = useFamily(book.familyId)
  // In the Modern style of Recipes the owner can pick, the book is its kitchens in rings over a wall of dishes.
  const modern = useMe().data?.recipesStyle === 'modern'
  const count = book.recipes.length
  // Only people in its own circle add to it; someone it's shared with from another circle reads it.
  const canAdd = book.canAdd !== false
  const forWhom = book.sharedWith ? audienceSaid(book, book.mine ? 'Only you' : '') : (book.space?.name ?? '')
  // Breakfast, desserts...: the meals its recipes are for, to show one at a time. Kept in the address (?meal=).
  const [params, setParams] = useSearchParams()
  const meals = MEALS.filter((meal) => book.recipes.some((x) => x.meals.includes(meal)))
  const asked = params.get('meal')
  const meal = isMeal(asked) && meals.includes(asked) ? asked : null
  const shown = meal ? book.recipes.filter((x) => x.meals.includes(meal)) : book.recipes
  const pick = (next: Meal | null) =>
    setParams(
      (p) => {
        if (next) p.set('meal', next)
        else p.delete('meal')
        return p
      },
      { replace: true, preventScrollReset: true },
    )
  const home = useItemHome(book)
  usePageFrame(home, {
    title: book.title,
    back:
      canAdd || home !== book.familyId
        ? { to: `/circle/${home}/recipes`, label: 'Back to recipes' }
        : { to: '/', label: 'Back' },
  })
  useEffect(
    () => rememberVisit(book.familyId, { to: `/book/${book.id}`, title: book.title, kind: 'book' }),
    [book.familyId, book.id, book.title],
  )

  return (
    <div className="flex flex-col gap-8 pt-6 pb-10 nav:gap-10 nav:pt-12">
      <header className={cn(page, 'flex flex-col gap-6')}>
        <div className="flex min-h-12 items-center justify-end gap-2">
          <Link
            to={`/circle/${book.familyId}/recipes`}
            className="mr-auto hidden h-10 items-center gap-1.5 rounded-full bg-secondary px-4 text-sm font-medium transition-colors outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 nav:inline-flex"
          >
            <ArrowLeft className="size-4" aria-hidden /> Recipes
          </Link>
          {count > 0 && family.data && (
            <Button variant="outline" size="lg" className="h-12" onClick={() => setCookbook(true)}>
              <BookHeart aria-hidden /> Make a cookbook
            </Button>
          )}
          {book.mine && (
            <Button
              variant="outline"
              size="icon"
              className="size-12"
              aria-label="Book settings"
              onClick={() => setSettings(true)}
            >
              <Settings2 aria-hidden />
            </Button>
          )}
          {canAdd && (
            <CreateAction
              section="recipes"
              title="Add a recipe"
              actions={[
                { label: 'Scan a recipe card', icon: ScanText, onSelect: () => navigate(`/book/${book.id}/new`) },
                {
                  label: 'Write a recipe',
                  icon: NotebookPen,
                  onSelect: () => navigate(`/book/${book.id}/new?write=1`),
                },
              ]}
            />
          )}
        </div>
        <div className="flex flex-col items-center gap-3 text-center nav:items-start nav:text-left">
          <h1 className="text-display text-balance">{book.title}</h1>
          <p className="flex flex-wrap justify-center gap-x-2 text-sub text-muted-foreground">
            {!modern && <span>{plural(count, 'recipe')}</span>}
            {!modern && forWhom && <span aria-hidden="true">·</span>}
            {forWhom && <span>For {forWhom}</span>}
          </p>
        </div>
        {!modern && <Cooks book={book} />}
      </header>

      <div className={page}>
        {count === 0 ? (
          <div className="flex flex-col items-center gap-5 rounded-[2.5rem] border-2 border-dashed border-input px-6 py-14 text-center sm:py-20">
            <span className="grid size-16 place-items-center rounded-full bg-secondary">
              <Camera className="size-7" strokeWidth={1.6} aria-hidden />
            </span>
            <p className="max-w-md text-title font-semibold tracking-tight text-balance">
              Every circle has a recipe worth keeping. Photograph the first card.
            </p>
            {canAdd && (
              <Button asChild size="lg">
                <Link to={`/book/${book.id}/new`}>
                  <Plus aria-hidden /> Add a recipe
                </Link>
              </Button>
            )}
            <p className="whisper">Keep the card. Share the recipe.</p>
          </div>
        ) : modern ? (
          <ModernBook book={book} />
        ) : (
          <div className="flex flex-col gap-6">
            {meals.length > 0 && <MealFilter meals={meals} value={meal} onChange={pick} />}
            <ul
              aria-label="Recipes"
              className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:grid-cols-[repeat(auto-fill,minmax(13rem,1fr))]"
            >
              {shown.map((recipe, i) => (
                <li key={recipe.id}>
                  <RecipeTile
                    recipe={recipe}
                    index={i}
                    line={recipe.from ? `From ${recipe.from.name}` : `Added by ${recipe.by}`}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {family.data && (
        <NewCookbook
          key={String(cookbook)}
          open={cookbook}
          onOpenChange={setCookbook}
          family={family.data}
          space={family.data.spaces.find((x) => x.id === book.space?.id) ?? null}
          name={book.title}
          recipeIds={book.recipes.map((x) => x.id)}
        />
      )}
      {book.mine && (
        <Dialog open={settings} onOpenChange={setSettings}>
          <DialogContent className="max-h-[90svh] overflow-y-auto">
            <BookSettings book={book} onSaved={() => setSettings(false)} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

// All the recipes, or only those for one meal. Scrolls sideways on a phone when there are many.
function MealFilter({
  meals,
  value,
  onChange,
}: {
  meals: Meal[]
  value: Meal | null
  onChange: (meal: Meal | null) => void
}) {
  const options: [Meal | null, string][] = [[null, 'All'], ...meals.map((m): [Meal, string] => [m, MEAL_NAMES[m].one])]
  return (
    <div
      role="group"
      aria-label="Show recipes for"
      className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0"
    >
      {options.map(([option, name]) => (
        <button
          key={option ?? 'all'}
          type="button"
          aria-pressed={value === option}
          onClick={() => onChange(option)}
          className="min-h-9 shrink-0 rounded-full border border-input px-4 text-sm font-medium transition-colors outline-none hover:bg-secondary focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-pressed:border-brand aria-pressed:bg-brand aria-pressed:text-brand-foreground pointer-coarse:min-h-11"
        >
          {name}
        </button>
      ))}
    </div>
  )
}

// Whose recipes are in the book, as a row of faces like the people in an album. Each opens their page.
function Cooks({ book }: { book: Book }) {
  const seen = new Map<string, From>()
  for (const recipe of book.recipes) if (recipe.from) seen.set(recipe.from.id, recipe.from)
  const people = [...seen.values()]
  if (people.length === 0) return null
  const shown = people.slice(0, 6)
  return (
    <div className="flex items-center gap-3 rounded-[1.75rem] border px-4 py-3 sm:gap-5 sm:px-5">
      <p className="hidden shrink-0 text-sm leading-tight font-medium sm:block">
        Recipes
        <span className="block font-normal text-muted-foreground">from</span>
      </p>
      <ul className="flex min-w-0 items-center gap-1.5" aria-label="Whose recipes are here">
        {shown.map((p, i) => (
          <li key={p.id} className={cn(i >= 4 && 'max-sm:hidden')}>
            <Link
              to={`/person/${p.id}`}
              aria-label={`Open ${p.name}’s page`}
              title={p.name}
              className="block rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              <Avatar person={p} className="size-9 text-base shadow-none ring-2 ring-background sm:size-10" />
            </Link>
          </li>
        ))}
        {people.length > shown.length && (
          <li className="text-xs text-muted-foreground">+{people.length - shown.length}</li>
        )}
      </ul>
    </div>
  )
}

// Looking after the book: its name, who it's for, and taking it down. Only the person who started it can.
function BookSettings({ book, onSaved }: { book: Book; onSaved: () => void }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [title, setTitle] = useState(book.title)
  const [who, setWho] = useState<Audience>(() =>
    book.sharedWith ? audienceFrom(book) : { everyone: false, spaceIds: book.space ? [book.space.id] : [] },
  )
  const save = useMutation({
    mutationFn: () =>
      api(`/books/${book.id}`, { method: 'PATCH', body: { title: title.trim(), ...audienceBody(who) } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['book', book.id] })
      await queryClient.invalidateQueries({ queryKey: ['books', book.familyId] })
      onSaved()
    },
  })
  const remove = useMutation({
    mutationFn: () => api(`/books/${book.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      forgetVisit(book.familyId, `/book/${book.id}`)
      await queryClient.invalidateQueries({ queryKey: ['books', book.familyId] })
      navigate(`/circle/${book.familyId}/recipes`, { replace: true })
    },
  })
  return (
    <div className="flex flex-col gap-6">
      <DialogHeader>
        <DialogTitle>Book settings</DialogTitle>
        <DialogDescription>Only you can change these, as the person who started this book.</DialogDescription>
      </DialogHeader>
      <form
        className="grid gap-5"
        onSubmit={(e) => {
          e.preventDefault()
          save.mutate()
        }}
      >
        <div className="grid gap-2">
          <Label htmlFor="book-rename">Book name</Label>
          <Input id="book-rename" required maxLength={80} value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="grid gap-2">
          <p className="text-sm font-medium">Who’s this for?</p>
          <ShareWith here={book.familyId} value={who} onChange={setWho} onlyMeHint="Just you, until you share it" />
          <p className="text-sub text-muted-foreground">Everyone who sees the book sees every recipe in it.</p>
        </div>
        <FormError error={save.error} />
        <Button type="submit" size="lg" disabled={save.isPending} className="self-start">
          Save changes
        </Button>
      </form>
      <div className="flex flex-col gap-2 border-t pt-5">
        <FormError error={remove.error} />
        <ConfirmButton
          label="Delete this book"
          title={`Delete ${book.title}?`}
          description="The book and its recipes will be removed for everyone in the circle."
          confirm="Delete book"
          disabled={remove.isPending}
          onConfirm={() => remove.mutate()}
        />
      </div>
    </div>
  )
}
